import { useEffect, useMemo, useState } from 'react';

import { useCatalogImageRotation } from './CatalogImageRotationContext';
import { IMAGE_TRANSITION_DURATION_MS } from '../../constants/catalogImageRotation';

const ROTATING_MESSAGES = [
  {
    eyebrow: 'Catalog pulse',
    title: 'Trending now',
    description: 'High-demand frames are moving quickly across the live catalog.',
  },
  {
    eyebrow: 'Catalog pulse',
    title: 'Fast selling',
    description: 'Browse the styles customers are adding to cart the most this week.',
  },
  {
    eyebrow: 'Catalog pulse',
    title: 'Limited deals',
    description: 'Catch value-focused picks before the stock window closes.',
  },
  {
    eyebrow: 'Catalog pulse',
    title: 'New arrivals',
    description: 'Fresh silhouettes just landed and are ready to explore.',
  },
];

const ATTENTION_LOOP_IMAGE_PAIRS = [
  ['/Oakley-AL1.webp', '/Oakley-AL2.webp'],
  ['/Oakley-AL3.webp', '/Oakley-AL4.webp'],
  ['/Oakley-AL5.webp', '/Oakley-AL6.webp'],
  ['/Oakley-AL7.webp', '/Oakley-AL8.webp'],
  ['/Oakley-AL9.webp', '/Oakley-AL10.webp'],
  ['/Oakley-AL11.webp', '/Oakley-AL12.webp'],
  ['/Oakley-AL13.webp', '/Oakley-AL14.webp'],
];
const MOBILE_IMAGE_COLUMN_WIDTH_CLASS = 'grid-cols-[minmax(0,1fr)_112px]';

const getImageAlt = (imagePath) => {
  const imageName = imagePath.split('/').pop()?.replace('.webp', '') ?? 'Oakley';
  return `${imageName} Oakley sunglasses lifestyle image`;
};

const AttentionLoopImageCard = ({ currentImage, previousImage, isTransitionActive }) => {
  const transitionStyle = {
    transitionDuration: `${IMAGE_TRANSITION_DURATION_MS}ms`,
  };

  return (
    <div className="relative aspect-[16/10] overflow-hidden rounded-[16px] border border-white/20 bg-white/[0.06] shadow-product-normal ring-1 ring-inset ring-white/10 md:rounded-[18px]">
      {previousImage ? (
        <img
          src={previousImage}
          alt={getImageAlt(previousImage)}
          className={`absolute inset-0 h-full w-full object-cover transition-all ease-out ${
            isTransitionActive ? 'scale-[1.02] opacity-0' : 'scale-100 opacity-100'
          }`}
          style={transitionStyle}
          loading="lazy"
          decoding="async"
        />
      ) : null}

      <img
        src={currentImage}
        alt={getImageAlt(currentImage)}
        className={`absolute inset-0 h-full w-full object-cover transition-all ease-out ${
          previousImage
            ? isTransitionActive
              ? 'scale-100 opacity-100'
              : 'scale-[1.02] opacity-0'
            : 'scale-100 opacity-100'
        }`}
        style={transitionStyle}
        loading="lazy"
        decoding="async"
      />

      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(0,0,0,0.18)_100%)]" />
    </div>
  );
};

const DynamicCatalogBanner = ({ sequenceIndex = 0 }) => {
  const { rotationTick } = useCatalogImageRotation();
  const activePairIndex = (sequenceIndex + rotationTick) % ATTENTION_LOOP_IMAGE_PAIRS.length;
  const activeMessage = ROTATING_MESSAGES[activePairIndex % ROTATING_MESSAGES.length];
  const activePair = ATTENTION_LOOP_IMAGE_PAIRS[activePairIndex];
  const nextPair = ATTENTION_LOOP_IMAGE_PAIRS[(activePairIndex + 1) % ATTENTION_LOOP_IMAGE_PAIRS.length];
  const [displayPair, setDisplayPair] = useState(activePair);
  const [previousPair, setPreviousPair] = useState(null);
  const [isPairTransitionActive, setIsPairTransitionActive] = useState(false);

  useEffect(() => {
    if (displayPair[0] === activePair[0] && displayPair[1] === activePair[1]) {
      return undefined;
    }

    setPreviousPair(displayPair);
    setDisplayPair(activePair);
    setIsPairTransitionActive(false);

    const activateTransition = window.requestAnimationFrame(() => {
      setIsPairTransitionActive(true);
    });

    const clearPreviousPair = window.setTimeout(() => {
      setPreviousPair(null);
      setIsPairTransitionActive(false);
    }, IMAGE_TRANSITION_DURATION_MS);

    return () => {
      window.cancelAnimationFrame(activateTransition);
      window.clearTimeout(clearPreviousPair);
    };
  }, [activePair, displayPair]);

  useEffect(() => {
    nextPair.forEach((imagePath) => {
      const preloadImage = new window.Image();
      preloadImage.src = imagePath;
    });
  }, [nextPair]);

  const renderedImages = useMemo(
    () => [
      {
        currentImage: displayPair[0],
        previousImage: previousPair?.[0] ?? null,
      },
      {
        currentImage: displayPair[1],
        previousImage: previousPair?.[1] ?? null,
      },
    ],
    [displayPair, previousPair]
  );

  return (
    <section
      data-catalog-attention-reference={sequenceIndex === 0 ? 'true' : undefined}
      className="relative overflow-hidden rounded-[24px] border border-black/5 bg-[#111111] text-white shadow-product-normal"
    >
      <div className="absolute inset-0 bg-[url('/OakleyLandscape.webp')] bg-cover bg-center bg-no-repeat" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.40)_0%,rgba(0,0,0,0.60)_100%)]" />

      <div
        className={`relative grid items-end gap-[12px] px-[16px] py-[16px] ${MOBILE_IMAGE_COLUMN_WIDTH_CLASS} md:grid-cols-[minmax(0,1fr)_240px] md:gap-[24px] md:px-[28px] md:py-[26px]`}
      >
        <div className="relative min-h-[148px] md:min-h-[132px]">
          <div className="absolute inset-0 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center rounded-pill border border-white/10 bg-white/10 px-[10px] py-[4px] text-[11px] font-semibold uppercase tracking-[0.18em] text-white/[0.78]">
                {activeMessage.eyebrow}
              </span>
              <h2 className="mt-[10px] max-w-[190px] text-[22px] font-bold leading-[1.02] tracking-tight md:mt-[12px] md:max-w-none md:text-[42px]">
                {activeMessage.title}
              </h2>
              <p className="mt-[6px] max-w-[180px] text-[12px] leading-[1.45] text-white/[0.78] md:mt-[8px] md:max-w-[620px] md:text-[16px] md:leading-[1.6]">
                {activeMessage.description}
              </p>
            </div>

            <div className="mt-[12px] flex items-center gap-[6px] md:mt-[16px] md:gap-[8px]">
              {ATTENTION_LOOP_IMAGE_PAIRS.map((_, dotIndex) => (
                <span
                  key={`indicator-${dotIndex}`}
                  className={`rounded-pill transition-all duration-300 ${
                    dotIndex === activePairIndex ? 'h-[5px] w-[22px] bg-white md:h-[6px] md:w-[28px]' : 'h-[5px] w-[5px] bg-white/30 md:h-[6px] md:w-[6px]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-[8px] md:gap-[10px]">
          {renderedImages.map((imageConfig) => (
            <AttentionLoopImageCard
              key={imageConfig.currentImage}
              currentImage={imageConfig.currentImage}
              previousImage={imageConfig.previousImage}
              isTransitionActive={isPairTransitionActive}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DynamicCatalogBanner;
